import { NgModule } from '@angular/core';
import { EffectsModule } from '@ngrx/effects';
import { routerReducer, RouterReducerState } from '@ngrx/router-store';
import { ActionReducer, ActionReducerMap, MetaReducer, StoreModule } from '@ngrx/store';

import { appActions } from './app/app.actions';
import { AppEffects } from './app/app.effects';
import { retainedStateForReset } from './app/reset-state';
import { AppEntitiesStateModule } from './app-entities/app-entities-state.module';
import { AppGlobalStateModule } from './app-global/app-global-state.module';
import { CapacitorAppEffects } from './capacitor/app/capacitor-app.effects';
import { devToolModules } from './dev-tools';

/**
 * This is the Global root state declared in storeModule.forRoot() in app.module.ts
 *
 * It is rare that anything should be added to this store.  If you find yourself in need of
 * adding a new prop here, please review app-global, app-entites, and app-layout.
 * This is where most new global state should be registered or in a domain specific module.
 *
 * Any questions can be directed to the senior engineers.
 */

export interface rootState {
  router: RouterReducerState;
}

// This function is used to intercept actions and update the
// state based on the action type BEFORE any feature reducer is called.
// In this case we will use it to reset state when the user logs out
// or switches companies.
function stateReducerMiddleware(reducer: ActionReducer<any>): ActionReducer<any> {
  return function (state, action) {
    if (action.type !== appActions.resetGlobalState.type) {
      return reducer(state, action);
    }

    // Feeding the root reducer a mostly-empty state makes every feature reducer return its own
    // initialState, because that is what `createReducer` does with `undefined`. Slices reset
    // themselves; nothing here needs to know what any slice's empty looks like.
    return reducer(retainedStateForReset(state), action);

    // ToDo: Phase 2 of switch company
    // Don't refresh the page, clear the company specific stores — clearCompanySpecificState.
    // switchCompany currently reloads the app (AppEffects.reloadApp$), so there is nothing to do
    // here for it.
  };
}
export const metaReducers: MetaReducer<any>[] = [stateReducerMiddleware];

export const globalReducers: ActionReducerMap<rootState> = {
  router: routerReducer,
};

/**
 * `CapacitorAppEffects` registers its plugin listeners here, ahead of the consumers that read its
 * actions from `AppGlobalStateModule`'s `forFeature`. The actions stream is hot, and `appUrlOpen` is
 * retained until consumed — so a cold-start deep link dispatched before a consumer subscribed would be
 * gone. It cannot be: delivery costs a bridge round trip, and every consumer is registered eagerly
 * below in the same synchronous module-init turn. Making any of them lazy breaks that.
 */
export const globalStateEffects = [CapacitorAppEffects, AppEffects];

@NgModule({
  imports: [
    StoreModule.forRoot(globalReducers, { metaReducers }),
    EffectsModule.forRoot(globalStateEffects),
    devToolModules, // import for ReduxDevTools, this isreplaced with empty array in the prod environment during build
    AppGlobalStateModule,
    AppEntitiesStateModule,
  ],
})
export class PlatformStateModule {}
